import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDate } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, Search, User, Pencil, Trash2, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { CHANNELS } from "../../lib/channels";
import { toast } from "sonner";
import { ConfirmDialog, ChannelBadge } from "../../components/common";

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [chF, setChF] = useState("all");
  const [del, setDel] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", district: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/customers", { params: q ? { q } : {} });
    setList(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const EMPTY = { name: "", phone: "", email: "", address: "", city: "", district: "", notes: "" };
  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name || "", phone: c.phone || "", email: c.email || "", address: c.address || "", city: c.city || "", district: c.district || "", notes: c.notes || "" });
    setOpen(true);
  };
  const remove = async () => {
    try { await api.delete(`/customers/${del.id}`); toast.success("Müşteri silindi"); setDel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };
  const download = async () => {
    const res = await api.get("/export-xlsx/customers", { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = "musteriler.xlsx"; a.click();
  };
  const shown = list.filter((c) => chF === "all" || (c.source_channel || "belirtilmemis") === chF);

  const save = async () => {
    if (!form.name || !form.phone) { toast.error("İsim ve telefon zorunlu"); return; }
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success(editId ? "Müşteri güncellendi" : "Müşteri eklendi");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", address: "", city: "", district: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">CRM</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Müşteriler</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={download} data-testid="export-customers"><Download className="h-4 w-4 mr-2" />Excel</Button>
          <Button onClick={openCreate} data-testid="new-customer-button" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" /> Yeni Müşteri
          </Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Müşteriyi Düzenle" : "Yeni Müşteri"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ad Soyad / Firma *</Label><Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Telefon *</Label><Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div><Label>E-posta</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Şehir</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div><Label>İlçe</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></div>
              </div>
              <div><Label>Adres</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Notlar</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={save} data-testid="save-customer-button" className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border border-slate-200 shadow-none">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="İsim, telefon veya e-posta ile ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="customer-search" />
          </div>
          <Select value={chF} onValueChange={setChF}>
            <SelectTrigger className="w-48" data-testid="customer-channel-filter"><SelectValue placeholder="Kanal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm kanallar</SelectItem>
              {Object.entries(CHANNELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Müşteri</th>
                <th className="px-4 py-3 font-semibold">İletişim</th>
                <th className="px-4 py-3 font-semibold">Adres</th>
                <th className="px-4 py-3 font-semibold">Kanal</th>
                <th className="px-4 py-3 font-semibold">Eklendi</th>
                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12">
                  <User className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <div className="text-slate-500">Müşteri bulunamadı</div>
                </td></tr>
              )}
              {shown.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <Link to={`/admin/customers/${c.id}`} className="font-medium text-blue-700 hover:underline" data-testid={`customer-link-${c.id}`}>{c.name}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <div>{c.phone}</div>
                    <div className="text-xs text-slate-500">{c.email || "-"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{[c.address, c.district, c.city].filter(Boolean).join(", ") || "-"}</td>
                  <td className="px-4 py-3">{c.source_channel ? <ChannelBadge channel={c.source_channel} /> : <span className="text-slate-300">-</span>}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setDel(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title={`${del?.name} silinsin mi?`}
        description="Müşteri ve kayıtlı cihazları silinir. Geçmiş servis kayıtları korunur." onConfirm={remove} />
    </div>
  );
}
